import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Activity, Armchair, BookMarked, ChevronRight, DollarSign, History, LayoutDashboard, LogOut, RefreshCcw, Route as RouteIcon, Settings, Tags, TrendingUp, Users } from 'lucide-react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LOADING_LABELS } from '@kexiaoman/shared';
import { api, adminApi } from '@/lib/api';
import { cn } from '@/lib/utils';

type NavItem = { to: string; label: string; icon: typeof LayoutDashboard; end?: boolean };

const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: '运营',
    items: [
      { to: '/', label: '概览', icon: LayoutDashboard, end: true },
      { to: '/seats', label: '席位', icon: Armchair },
      { to: '/users', label: '用户', icon: Users },
      { to: '/channels', label: '渠道', icon: Tags },
    ],
  },
  {
    label: '计费',
    items: [
      { to: '/pricing', label: '模型定价', icon: DollarSign },
      { to: '/pricing-history', label: '改价历史', icon: History },
      { to: '/cost', label: '成本', icon: TrendingUp },
      { to: '/events', label: '使用事件', icon: Activity },
      { to: '/routes', label: '模型路由', icon: RouteIcon },
    ],
  },
  {
    label: '系统',
    items: [
      { to: '/logs', label: '审计日志', icon: BookMarked },
      { to: '/settings', label: '设置', icon: Settings },
    ],
  },
];

const ALL_NAV = NAV_GROUPS.flatMap((g) => g.items.map((it) => ({ ...it, group: g.label })));

function useActiveNav() {
  const { pathname } = useLocation();
  return (
    ALL_NAV.find((it) => (it.end ? pathname === it.to : pathname === it.to || pathname.startsWith(`${it.to}/`))) ??
    ALL_NAV[0]
  );
}

export function AppLayout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const active = useActiveNav();

  const logout = useMutation({
    mutationFn: () => adminApi.logout(),
    onSettled: () => {
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });

  const reload = useMutation({
    mutationFn: () => api<{ ok: true }>('/admin/config/reload', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  return (
    <div className="flex h-screen bg-background text-foreground">
      <aside className="flex w-60 flex-shrink-0 flex-col border-r border-border bg-sidebar">
        <div className="flex h-14 items-center border-b border-border px-5">
          <span className="text-sm font-semibold tracking-tight">喜爱云 · 管理</span>
        </div>
        <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-5">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="space-y-1">
              <div className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                {group.label}
              </div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors',
                      isActive
                        ? 'bg-card font-medium text-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-card/60 hover:text-foreground',
                    )
                  }
                >
                  <item.icon className="h-4 w-4 flex-shrink-0" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="border-t border-border px-5 py-3 text-xs text-muted-foreground">
          operator: <span className="font-medium text-foreground">admin</span>
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 flex-shrink-0 items-center justify-between border-b border-border bg-card/80 px-6 backdrop-blur">
          <div className="flex items-center gap-1.5 text-sm">
            <span className="text-muted-foreground">{active.group}</span>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
            <span className="font-medium">{active.label}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => reload.mutate()} disabled={reload.isPending}>
              <RefreshCcw className={cn('h-4 w-4 mr-1', reload.isPending && 'animate-spin')} />
              {reload.isPending ? LOADING_LABELS.refreshing : '刷新缓存'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => logout.mutate()} disabled={logout.isPending}>
              <LogOut className="h-4 w-4 mr-1" />
              登出
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          <div className="mx-auto w-full max-w-[1440px] p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
